import { createSlice } from "@reduxjs/toolkit";
import type { EventApi } from "@fullcalendar/react";

import { TABS } from "@/component/drawer";
import { CalendarView, RosterView } from "@/interfaces/IRoster";
import { Visit } from "@/interfaces/IServiceUser";
import { ZoneOption } from "@/interfaces/IZone";
import logger from "@/runtime-utils/observability/logger";
import {
  createVisitAsync,
  deleteManyVisitAsync,
  deleteVisitAsync,
  getScheduleVisitsAsync,
  getVisitsByOriginAsync,
  getZoneOptionsAsync,
  updateMultipleVisitsAsync,
  updateVisitAsync,
} from "../actions/roster";

type ReducerState<T = any> = {
  data: T;
  loading: boolean;
  error: any | null;
  total?: number;
  hasMoreData?: boolean;
};

interface RosterReducerState {
  // Define your reducer state here
  zones: ReducerState<ZoneOption[]>;
  visitsByOrigin: ReducerState<Visit[]>;
  visits: ReducerState<Visit[]>;
  openCarerModal: boolean;
  drawerOpen: boolean;
  currentView: RosterView;
  calendarView: CalendarView;
  selectedVisit: EventApi | null;
  selectedZones: string[];
  currentTab: string;
  showTravelTime: boolean;
  showInactiveUser: boolean;
}

export const upsertVisitById = (visits: Visit[], incoming?: Visit) => {
  if (!incoming?._id) return visits;
  const index = visits.findIndex((visit) => visit?._id === incoming._id);
  if (index === -1) return [...visits, incoming];
  return visits.map((visit, currentIndex) =>
    currentIndex === index ? { ...visit, ...incoming } : visit
  );
};

const initialState: RosterReducerState = {
  // Set initial state here
  zones: {
    data: [],
    loading: false,
    error: null,
  },
  visits: {
    data: [],
    loading: false,
    error: null,
  },
  visitsByOrigin: {
    data: [],
    loading: false,
    error: null,
  },
  openCarerModal: false,
  drawerOpen: false,
  currentView: RosterView.STAFF,
  // currentView: RosterView.SERVICE,
  calendarView: CalendarView.DAY,
  selectedVisit: null,
  selectedZones: [],
  currentTab: TABS[0].value,
  showTravelTime: false,
  showInactiveUser: false,
};

export const rosterReducer = createSlice({
  name: "rosterReducer",
  initialState,
  reducers: {
    // Define reducer actions here
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    setCalendarView: (state, action) => {
      state.calendarView = action.payload;
    },
    setSelectedVisit: (state, action) => {
      state.selectedVisit = action.payload;
    },
    setSelectedZones: (state, action) => {
      state.selectedZones = action.payload;
    },
    toggleCarerModal: (state, action) => {
      state.openCarerModal = action.payload;
    },
    toggleRosterDrawer: (state, action) => {
      state.drawerOpen = action.payload;
    },
    updateSingleVisit: (state, action) => {
      const { _id } = action.payload;

      const updatedVisits = state.visits.data.map((v) => {
        if (v._id === _id) {
          return {
            ...v,
            ...action.payload,
          };
        }
        return v;
      });

      state.visits.data = updatedVisits;
    },
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload;
    },
    toggleTravelTime: (state, action) => {
      state.showTravelTime = action.payload;
    },
    toggleInactiveUser: (state, action) => {
      state.showInactiveUser = action.payload;
    },

    addVisitLocally: (state, action) => {
      state.visits.data = upsertVisitById(state.visits.data, action.payload);
    },

    updateVisitLocally: (state, action) => {
      const updatedVisit = action.payload;
      state.visits.data = state.visits.data.map((v) =>
        v._id === updatedVisit._id ? { ...v, ...updatedVisit } : v
      );
    },

    updateMultipleVisitsLocally: (state, action) => {
      const updatedVisits = action.payload; // array of Visit
      const updatedIds = updatedVisits.map((v: Visit) => v._id);

      state.visits.data = state.visits.data.map((v) =>
        updatedIds.includes(v._id)
          ? { ...v, ...updatedVisits.find((uv: Visit) => uv._id === v._id) }
          : v
      );
    },

    deleteVisitLocally: (state, action) => {
      const idToDelete = action.payload;
      state.visits.data = state.visits.data.filter((v) => v._id !== idToDelete);
    },

    setVisitsByOrigin: (state, action) => {
      state.visitsByOrigin.data = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getZoneOptionsAsync.pending, (state) => {
        state.zones.loading = true;
        state.zones.error = null;
      })
      .addCase(getZoneOptionsAsync.fulfilled, (state, action) => {
        state.zones.loading = false;
        state.zones.data = action.payload?.data;
      })
      .addCase(getZoneOptionsAsync.rejected, (state, action) => {
        state.zones.loading = false;
        state.zones.error = action.error;
      })

      .addCase(getScheduleVisitsAsync.pending, (state) => {
        state.visits.loading = true;
        state.visits.error = null;
        state.visits.data = [];
      })
      .addCase(getScheduleVisitsAsync.fulfilled, (state, action) => {
        state.visits.loading = false;
        state.visits.data = action.payload?.data;
      })
      .addCase(getScheduleVisitsAsync.rejected, (state, action) => {
        state.visits.loading = false;
        state.visits.error = action.error;
        state.visits.data = [];
      })

      .addCase(getVisitsByOriginAsync.pending, (state) => {
        state.visitsByOrigin.loading = true;
        state.visitsByOrigin.error = null;
      })
      .addCase(getVisitsByOriginAsync.fulfilled, (state, action) => {
        state.visitsByOrigin.loading = false;
        state.visitsByOrigin.data = action.payload?.data;
      })
      .addCase(getVisitsByOriginAsync.rejected, (state, action) => {
        state.visitsByOrigin.loading = false;
        state.visitsByOrigin.error =
          action.error.message || "Failed to fetch data";
      })

      .addCase(createVisitAsync.pending, (state) => {
        state.visits.error = null;
      })
      .addCase(createVisitAsync.fulfilled, (state, action) => {
        try {
          const visit = action.payload?.data as Visit | undefined;
          logger.info(
            {
              event: "schedule_visit.create.fulfilled",
              visitId: visit?._id ?? null,
            },
            "createVisitAsync"
          );
          state.visits.data = upsertVisitById(state.visits.data, visit);

          // const origin = action.payload?.data?.origin;

          // logger.debug({
          //   visitId: state.selectedVisit?.extendedProps,
          //   origin,
          //   payload: action.payload?.data,
          // });

          // if (state.selectedVisit?.extendedProps?._id === origin) {
          // state.selectedVisit?.setExtendedProp(
          //   "anotherCarers",
          //   action.payload?.data?.anotherCarers
          // );
          // state.selectedVisit?.setExtendedProp(
          //   "carerAssigned",
          //   action.payload?.data?.carerAssigned
          // );
          // }
        } catch (error) {
          logger.error({ error }, "createVisitAsync error");
        }
      })
      .addCase(createVisitAsync.rejected, (state, action) => {
        state.visits.error = action.error;
      })
      .addCase(updateVisitAsync.pending, (state) => {
        state.visits.error = null;
      })
      .addCase(updateVisitAsync.fulfilled, (state, action) => {
        try {
          const { _id } = action.payload?.data;

          const updatedVisits = state.visits.data.map((v) => {
            if (v._id === _id) {
              return {
                ...v,
                ...action.payload?.data,
              };
            }
            return v;
          });

          state.visits.data = updatedVisits;
        } catch (error) {
          logger.error({ error }, "updateVisitAsync error");
        }
      })
      .addCase(updateVisitAsync.rejected, (state, action) => {
        state.visits.error = action.error;
      })
      .addCase(updateMultipleVisitsAsync.pending, (state) => {
        state.visits.error = null;
      })
      .addCase(updateMultipleVisitsAsync.fulfilled, (state, action) => {
        try {
          const updatedIds =
            action.payload?.data?.map((v: Visit) => v._id) || [];

          const updatedVisits = state.visits.data.map((v) => {
            if (updatedIds.includes(v._id)) {
              const updatedVisit = action.payload?.data.find(
                (uv: Visit) => uv._id === v._id
              );
              return {
                ...v,
                ...updatedVisit,
              };
            }
            return v;
          });

          state.visits.data = updatedVisits;
        } catch (error) {
          logger.error({ error }, "updateMultipleVisitsAsync error");
        }
      })
      .addCase(updateMultipleVisitsAsync.rejected, (state, action) => {
        state.visits.error = action.error;
      })
      .addCase(deleteVisitAsync.fulfilled, (state, action) => {
        state.visits.data = state.visits.data.filter(
          (v) => v._id !== action.payload
        );
      })
      .addCase(deleteManyVisitAsync.fulfilled, (state, action) => {
        const { ids } = action.payload;

        state.visits.data = state.visits.data.filter(
          (v) => !ids.includes(v._id)
        );
        state.visitsByOrigin.data = state.visitsByOrigin.data.filter(
          (v) => !ids.includes(v._id)
        );
      });
  },
});

export const {
  setCurrentView,
  setCalendarView,
  setSelectedVisit,
  setSelectedZones,
  toggleCarerModal,
  toggleRosterDrawer,
  updateSingleVisit,
  setCurrentTab,
  addVisitLocally,
  updateVisitLocally,
  updateMultipleVisitsLocally,
  deleteVisitLocally,
  toggleTravelTime,
  toggleInactiveUser,
  setVisitsByOrigin,
} = rosterReducer.actions;
export default rosterReducer.reducer;
